import { supabase } from '../lib/supabase';
import { getTenantUserId } from './formsService';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  return Object.keys(obj).reduce((acc, key) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    acc[camelKey] = typeof obj[key] === 'object' ? toCamelCase(obj[key]) : obj[key];
    return acc;
  }, {});
};

const emptyMetrics = {
  viewCount: 0,
  clickCount: 0,
  submissionCount: 0,
  conversionRate: 0,
};

const normaliseVariant = (variant) => {
  const parsed = toCamelCase(variant);
  const metric = Array.isArray(parsed?.abTestMetrics)
    ? parsed.abTestMetrics[0]
    : parsed?.abTestMetrics;

  return {
    ...parsed,
    form: parsed?.forms || null,
    metrics: metric ? { ...emptyMetrics, ...metric } : emptyMetrics,
  };
};

const withSummary = (test) => {
  const parsed = toCamelCase(test);
  const variants = (parsed?.abTestVariants || parsed?.variants || []).map(normaliseVariant);
  const summary = variants.reduce((total, variant) => ({
    views: total.views + Number(variant.metrics?.viewCount || 0),
    clicks: total.clicks + Number(variant.metrics?.clickCount || 0),
    submissions: total.submissions + Number(variant.metrics?.submissionCount || 0),
  }), { views: 0, clicks: 0, submissions: 0 });

  return {
    ...parsed,
    variants,
    variantCount: variants.length,
    summary: {
      ...summary,
      conversionRate: summary.views ? (summary.submissions / summary.views) * 100 : 0,
    },
  };
};

const queryWithVariants = `
  *,
  ab_test_variants(
    id,
    variant_name,
    form_id,
    traffic_percentage,
    is_control,
    subject_line,
    email_content,
    forms!ab_test_variants_form_id_fkey(id, title, published),
    ab_test_metrics(
      view_count,
      click_count,
      submission_count,
      conversion_rate,
      opened_count,
      clicked_count,
      conversion_count
    )
  )
`;

const currentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) throw new Error('Not authenticated');
  return { user, tenantId: await getTenantUserId() };
};

export const abTestingService = {
  async getAllTests() {
    const { tenantId } = await currentUser();
    const { data, error } = await supabase
      .from('ab_tests')
      .select(queryWithVariants)
      .eq('created_by', tenantId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message || 'Failed to load A/B tests');
    return (data || []).map(withSummary);
  },

  async getTestById(testId) {
    const { tenantId } = await currentUser();
    const { data, error } = await supabase
      .from('ab_tests')
      .select(queryWithVariants)
      .eq('id', testId)
      .eq('created_by', tenantId)
      .maybeSingle();

    if (error) throw new Error(error.message || 'Failed to load this A/B test');
    return data ? withSummary(data) : null;
  },

  async createFormTest({ testName, description, formAId, formBId, trafficA }) {
    const { tenantId } = await currentUser();
    const name = String(testName || '').trim();
    const splitA = Number(trafficA);

    if (!name) throw new Error('Give your experiment a name.');
    if (!formAId || !formBId) throw new Error('Choose two published forms.');
    if (formAId === formBId) throw new Error('Choose two different forms.');
    if (!Number.isInteger(splitA) || splitA < 1 || splitA > 99) {
      throw new Error('Traffic split must give each form at least 1%.');
    }

    const { data: test, error: testError } = await supabase
      .from('ab_tests')
      .insert({
        test_name: name,
        description: String(description || '').trim() || null,
        status: 'active',
        test_kind: 'form',
        created_by: tenantId,
      })
      .select()
      .single();

    if (testError || !test) {
      throw new Error(testError?.message || 'Could not create the experiment.');
    }

    const variants = [
      {
        test_id: test.id,
        variant_name: 'Form A',
        form_id: formAId,
        traffic_percentage: splitA,
        is_control: true,
      },
      {
        test_id: test.id,
        variant_name: 'Form B',
        form_id: formBId,
        traffic_percentage: 100 - splitA,
        is_control: false,
      },
    ];

    const { data: createdVariants, error: variantsError } = await supabase
      .from('ab_test_variants')
      .insert(variants)
      .select('id');

    if (variantsError || !createdVariants || createdVariants.length !== 2) {
      await supabase.from('ab_tests').delete().eq('id', test.id).eq('created_by', tenantId);
      throw new Error(variantsError?.message || 'Could not save the two form variants.');
    }

    const { error: metricsError } = await supabase
      .from('ab_test_metrics')
      .insert(createdVariants.map((variant) => ({ variant_id: variant.id })));

    if (metricsError) {
      await supabase.from('ab_tests').delete().eq('id', test.id).eq('created_by', tenantId);
      throw new Error(metricsError.message || 'Could not initialise experiment metrics.');
    }

    return this.getTestById(test.id);
  },

  async updateTest(testId, updates) {
    const { tenantId } = await currentUser();
    const fieldMap = {
      testName: 'test_name',
      endDate: 'end_date',
      winnerVariantId: 'winner_variant_id',
      status: 'status',
      description: 'description',
    };
    const updateData = Object.entries(updates || {}).reduce((result, [key, value]) => {
      if (fieldMap[key]) result[fieldMap[key]] = value;
      return result;
    }, {});

    const { data, error } = await supabase
      .from('ab_tests')
      .update(updateData)
      .eq('id', testId)
      .eq('created_by', tenantId)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Could not update the experiment.');
    return toCamelCase(data);
  },

  async deleteTest(testId) {
    const { tenantId } = await currentUser();
    const { error } = await supabase
      .from('ab_tests')
      .delete()
      .eq('id', testId)
      .eq('created_by', tenantId);

    if (error) throw new Error(error.message || 'Could not delete the experiment.');
    return true;
  },

  async getTestStats() {
    const { tenantId } = await currentUser();
    const { data, error } = await supabase
      .from('ab_tests')
      .select('id, status')
      .eq('created_by', tenantId);

    if (error) throw new Error(error.message || 'Failed to load A/B test statistics');
    return {
      total: data?.length || 0,
      active: data?.filter((test) => test.status === 'active').length || 0,
      completed: data?.filter((test) => test.status === 'completed').length || 0,
      paused: data?.filter((test) => test.status === 'paused').length || 0,
    };
  },
};